import { Scorecard as ScorecardModel, ScorecardResult } from '../../models/Scorecard'
import { useGetScorecardResultsByFilterQuery } from '../../store/ai/scorecard'
import { useAppSelector } from '../../store/hooks'
import { selectAccountId } from '../../store/auth/authSlice'
import React from 'react'
import { Col, Container, Form, Modal as BootstrapModal, Row, Spinner } from 'react-bootstrap'
import { Button } from '../../components/Button/Button'
import { LinkButton } from '../../components/Button/LinkButton'
import { Modal } from '../../components/Modal/Modal'
import { formatDate, SOURCE_TYPE_MAPPING } from '../../common/clientProfile'

interface ClientProfilePropertyHistoryModalProps {
  scorecard: ScorecardModel
  scorecardResult: ScorecardResult
  sectionTitle: string
  propertyTitle: string
  property: string
  onHide: () => void
}

interface ClientProfilePropertyHistoryModalBodyProps {
  scorecard: ScorecardModel
  scorecardResult: ScorecardResult
  property: string
  sectionTitle: string
  propertyTitle: string
}

const ClientProfilePropertyHistoryModalBody: React.FC<ClientProfilePropertyHistoryModalBodyProps> = ({
  scorecard,
  scorecardResult,
  property,
  sectionTitle,
  propertyTitle,
}) => {
  const accountId = useAppSelector(selectAccountId)
  const { data: scorecardResults, isFetching: getScorecardsFetching } = useGetScorecardResultsByFilterQuery(
    {
      accountId,
      body: {
        scorecardId: scorecard.id,
        userId: scorecardResult.userId,
        field: property,
      },
    },
    { refetchOnMountOrArgChange: true },
  )

  if (getScorecardsFetching) {
    return <Spinner />
  }

  if (!scorecardResults) {
    return <div>No history available</div>
  }

  const sortedResults = [...scorecardResults]
    .filter((r) => {
      const change = r.changes[property]
      const metadata = r.resultMetadata[property]
      return change && metadata
    })
    .sort((a, b) => {
      return a.createdAt > b.createdAt ? -1 : 1
    })

  const getLastUpdatedAt = (scorecardResult: ScorecardResult) => {
    const prop = scorecardResult.resultMetadata[property]
    return prop['updatedAt']
  }

  const getUpdatedOn = (scorecardResult: ScorecardResult) => {
    const prop = scorecardResult.resultMetadata[property]
    const type = prop['source']['type']
    if (type === 'meeting') {
      const link = `/workspace/past-meetings/room/${prop['source']['roomId']}/session/${prop['source']['sessionId']}`
      return (
        <LinkButton variant='link' className='button-medium' to={link} target='_blank' rel='noopener noreferrer'>
          {SOURCE_TYPE_MAPPING[type] ?? type}
        </LinkButton>
      )
    }
    if (type === 'user') {
      const link = `/account/manage-team/users/${prop['source']['userId']}/view`
      return (
        <LinkButton variant='link' className='button-medium' to={link} target='_blank' rel='noopener noreferrer'>
          {SOURCE_TYPE_MAPPING[type] ?? type}
        </LinkButton>
      )
    }
    if (type === 'crossClientQuery') {
      const link = `/cross-client/executions/${prop['source']['executionId']}`
      return (
        <LinkButton variant='link' className='button-medium' to={link} target='_blank' rel='noopener noreferrer'>
          {SOURCE_TYPE_MAPPING[type] ?? type}
        </LinkButton>
      )
    }
    return <div>{SOURCE_TYPE_MAPPING[type] ?? type}</div>
  }

  return (
    <Container>
      <span>{sectionTitle}</span>
      {sortedResults.map((r, idx) => {
        const change = r.changes[property]
        const metadata = r.resultMetadata[property]
        if (!change || !metadata) {
          return null
        }
        return (
          <Row>
            <Col>
              <Form.Group className='mb-3'>
                <div className='d-flex justify-content-between'>
                  <div>{idx === 0 ? <Form.Label>{propertyTitle}</Form.Label> : null}</div>
                  <Row>
                    <Col className='d-flex align-items-center justify-content-center gap-2'>
                      <div className='text-capitalize me-1'>Updated at:{formatDate(new Date(getLastUpdatedAt(r)))}</div>
                      <div className='d-flex'>
                        <div className='text-capitalize align-items-center justify-content-center me-1'>type:</div>
                        {getUpdatedOn(r)}
                      </div>
                    </Col>
                  </Row>
                </div>
                {!Array.isArray(change['value']) ? (
                  <div className='d-flex gap-2 align-items-center'>
                    <i className='bi bi-arrow-return-right' />
                    <Form.Control type='text' value={change['value']} disabled style={{ backgroundColor: 'white' }} />
                  </div>
                ) : (
                  <div className='d-flex flex-column gap-2'>
                    {change['value'].map((value, index) => {
                      return (
                        <div key={index} className='d-flex gap-2 align-items-center'>
                          {/*{index === 0 && <i className='bi bi-arrow-return-right' />}*/}
                          <i className='bi bi-arrow-return-right' />
                          <Form.Control type='text' value={value} disabled style={{ backgroundColor: 'white' }} />
                        </div>
                      )
                    })}
                  </div>
                )}
              </Form.Group>
            </Col>
          </Row>
        )
      })}
    </Container>
  )
}

export const ClientProfilePropertyHistoryModal: React.FC<ClientProfilePropertyHistoryModalProps> = ({
  scorecard,
  scorecardResult,
  property,
  onHide,
  sectionTitle,
  propertyTitle,
}) => {
  return (
    <Modal
      size='lg'
      onHide={onHide}
      show={true}
      header={<BootstrapModal.Title className='w-100 d-flex align-items-center justify-content-center'>Input History</BootstrapModal.Title>}
      footer={
        <Button className='button-medium' variant='primary' onClick={onHide}>
          Close
        </Button>
      }
      body={
        <ClientProfilePropertyHistoryModalBody
          property={property}
          scorecard={scorecard}
          scorecardResult={scorecardResult}
          sectionTitle={sectionTitle}
          propertyTitle={propertyTitle}
        />
      }
    />
  )
}
